import { useCallback, useEffect, useRef } from 'react'

/**
 * A React Hook that provides a callback "isMounted" function to check whether
 * your component is still mounted.
 *
 * This is useful for situations where you have an asynchronous callback function
 * that should be "cancelled" if the component unmounts before it's done.
 *
 * For example, an async function that submits a request to update a record,
 * and then sends a request to refresh the data in a table. If it takes several
 * seconds to update the record, and the user clicks away to another screen
 * in the meantime, then there is no need to follow it up by refreshing the
 * table.
 *
 * @example
 * export function Foo(props) {
 *   const isMounted = useIsMounted();
 *   return <button onClick={async() => {
 *
 *     // This might take a while
 *     await doSomethingSlow();
 *
 *     // After the `await` finishes, the component may have unmounted due
 *     // to the user clicking away to another screen, closing a modal, etc.
 *     if (!isMounted()) {
 *       // If this component is no longer mounted, don't carry out any further
 *       // steps.
 *       return;
 *     }
 *
 *     doSomethingAsFollowUp();
 *   }}>Do a thing</button>;
 * }
 */
export function useIsMounted(): () => boolean {
  const isMounted = useRef<boolean>(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const callBack = useCallback(() => isMounted.current, [isMounted])

  return callBack
}
